import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { authFetch } from "@/lib/api";

const EntitlementsContext = createContext(null);

const DEFAULT_ENTITLEMENTS = {
  plan_tier: "free_core",
  trial_active: false,
  trial_ends_at: null,
  feature_grants: [],
  feature_denials: [],
  is_trial_eligible: false,
};

export function EntitlementsProvider({ children }) {
  const [entitlements, setEntitlements] = useState(DEFAULT_ENTITLEMENTS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refreshEntitlements = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch("/api/access/me/entitlements");
      if (!res.ok) throw new Error("Failed to load entitlements");
      const data = await res.json();
      const next = { ...DEFAULT_ENTITLEMENTS, ...data };
      setEntitlements(next);
      return next;
    } catch (err) {
      setError(err);
      setEntitlements(DEFAULT_ENTITLEMENTS);
      return DEFAULT_ENTITLEMENTS;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshEntitlements();
  }, [refreshEntitlements]);

  const startTrial = useCallback(async () => {
    const res = await authFetch("/api/access/trial/start", { method: "POST" });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.detail || data.message || "Could not start trial");
    }
    if (data.entitlements) {
      setEntitlements({ ...DEFAULT_ENTITLEMENTS, ...data.entitlements });
    } else {
      await refreshEntitlements();
    }
    return data;
  }, [refreshEntitlements]);

  const value = useMemo(() => {
    const isOwner = Boolean(entitlements.is_owner);
    const isResearchPlus = isOwner || entitlements.plan_tier === "research_plus";
    return {
      ...entitlements,
      is_owner: isOwner,
      isResearchPlus,
      loading,
      error,
      refreshEntitlements,
      startTrial,
    };
  }, [entitlements, loading, error, refreshEntitlements, startTrial]);

  return (
    <EntitlementsContext.Provider value={value}>
      {children}
    </EntitlementsContext.Provider>
  );
}

export function useEntitlements() {
  const context = useContext(EntitlementsContext);
  if (!context) {
    return {
      ...DEFAULT_ENTITLEMENTS,
      is_owner: false,
      isResearchPlus: false,
      loading: false,
      error: null,
      refreshEntitlements: async () => DEFAULT_ENTITLEMENTS,
      startTrial: async () => ({ status: "unavailable" }),
    };
  }
  return context;
}

